import React, { useState, useEffect, useLayoutEffect, useContext, useRef, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { WorkspaceContext } from '../../WorkspaceContext'
import { useCodeRunner } from '../../../coderunner/CodeRunnerContext'
import { createPortal } from 'react-dom'
import RenameTitle from '../../RenameTitle'
import RenameIcon from '../../../../shared/components/svgIcons/RenameIcon'
import '../sidebar.scss'
import { makeDragGhost } from '../makeDragGhost'
import Download from '../../../../shared/components/svgIcons/Download'
import TrashIcon from '../../../../shared/components/svgIcons/TrashIcon'
import SelectIcon from '../../../../shared/components/svgIcons/SelectIcon'
import { deleteResources } from '../../../../shared/utils/deleteResource'
import type { Snippet } from '../../snippets/SnippetContainer'
import type { FileRef } from '../../WorkspaceContext'
import api from '../../../../api'
import CheckIcon from '../../../../shared/components/svgIcons/CheckIcon'
import { normalizeMime } from '../../../../shared/utils/normalizeMime'
import { downloadFile, downloadSnippet, downloadMultiple } from '../DownloadHelper'
import { useAppUser } from '../../../../context/UserContext/UserContext'
import AddFileIcon from '../../../../shared/components/svgIcons/AddFileIcon'
import NewFolderIcon from '../../../../shared/components/svgIcons/NewFolderIcon'
import useCloseOnClickAway from '../../../../shared/hooks/UseCloseOnClickAway'
import { resolveSnippetFromTypedName } from '../../snippets/AcceptedLanguages'
import { isNameTakenInDirectory, NAME_TAKEN_MESSAGE } from '../../../../shared/utils/nameCollision'
import FileIconEditor from '../../../../shared/components/svgIcons/FileIconEditor'
import FolderIconEditor from '../../../../shared/components/svgIcons/FolderIconEditor'


import { getSnippetExtension } from '../../../../shared/utils/getFileIconAndExtensionHelper'
import { canAddResource, canCreateInFolder as canCreateInFolderShared } from '../../../../shared/utils/permissions'
import { useUploadEngine } from '../../files/UploadEngineContext'
import type { selectRes, Folder, ContextResource, RightClickData, RightClickTarget, FolderObj, renameObj, DragKind, DragPayload } from '../SideBarTypes'
import { collectFolderIdsFromTree, collectFolderSubtreeForSelection } from './FilesTabHelpers'
import '../sidebar.scss'
import styles from './FileExplorerTab.module.scss'
import { getSnippetIcon, getFileRefIcon } from '../../../../shared/utils/getFileIconAndExtensionHelper'

const MAX_ROOM_ITEMS = 50;
const FOLDER_LIMIT = 20;

const flattenFolderTree = (folders: Folder[]): Folder[] => {
    const result: Folder[] = [];

    const walk = (items: Folder[]) => {
        for (const folder of items) {
            result.push(folder);
            walk(folder.folders ?? []);
        }
    };

    walk(folders);
    return result;
};

const normalizeFolderPermission = (folder?: Pick<Folder, "folderPermission"> | null): "restricted" | "open" => {
    return folder?.folderPermission === "open" ? "open" : "restricted";
};


const FileExplorerTab = () => {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { ctheme, setLimitMessage, setLimitPopup, openSnippets, setOpenSnippets, setActiveSnippetId, Snippets, roomDetails, membersList, memberDetails, globalNewFilePopup, setGlobalNewFilePopup, globalNewSnipPopup, setGlobalNewSnipPopup, Files, Folders, setGlobalNewFolderPopup, openTabs, setOpenTabs, setActiveFileId, activeFileId, setDownloadFileErrorPopup, setPendingOpenId } = ctx;
    const { setActiveLanguage } = useCodeRunner();
    const { user: appUser } = useAppUser();
    const hasOwnerBypass = appUser?.plan === 'flashroom-owner';
    const { hoveredDropFolderId } = useUploadEngine();

    const { roomId } = useParams();
    const clientId = localStorage.getItem('flashroom_id')
    const [selectedIds, setSelectedIds] = useState<selectRes[]>([]);
    const [selectMode, setSelectMode] = useState(false);
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
    const hadSelectionRef = useRef(false);
    const [openMap, setOpenMap] = React.useState<Record<string, boolean>>({});
    const [newFolderPopup, setNewFolderPopup] = useState(false);
    const [folderName, setFolderName] = useState('');
    const resCanEditRef = useRef<Record<string, boolean>>({});

    // Which folder new items created from this sidebar's own toolbar/inline
    // creation should land in. Root/no-selection is represented as null.
    const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
    const filesTabRootRef = useRef<HTMLDivElement | null>(null);

    useCloseOnClickAway({
        targetRef: filesTabRootRef,
        onClose: () => setActiveFolderId(null),
    });

    type InlineCreateState = { type: 'file' | 'folder'; parentId: string | null };
    const [inlineCreate, setInlineCreate] = useState<InlineCreateState | null>(null);
    const [inlineCreateName, setInlineCreateName] = useState('');
    const [inlineCreateError, setInlineCreateError] = useState<string | null>(null);
    const [inlineCreateBusy, setInlineCreateBusy] = useState(false);
    const inlineCreateRef = useRef<HTMLDivElement | null>(null);
    const inlineCreateInputRef = useRef<HTMLInputElement | null>(null);

    const setInitialRole = () => {
        const role = memberDetails?.role;
        if (!role) return 'member';
        return role;
    }

    const [roleRef, setRoleRef] = useState<'owner' | 'admin' | 'member'>(setInitialRole());

    const resourcesList = useMemo<ContextResource[]>(() => [
        ...Snippets.map((s) => ({
            id: s._id,
            kind: "snippet" as const,
            creatorId: s.createdBy,
            parentId: s.folderParent === null ? null : String(s.folderParent),
            name: s.snippetName,
            raw: s,
        })),
        ...flattenFolderTree(Folders).map((f) => ({
            id: f._id,
            kind: "folder" as const,
            creatorId: f.creatorId,
            parentId: f.folderParent ?? null,
            name: f.fname,
            raw: f,
        })),
        ...Files.map((file) => ({
            id: file._id,
            kind: "file" as const,
            creatorId: file.uploaderClientId ?? "",
            parentId: file.folderParent ?? null,
            name: file.name,
            raw: file,
        })),
    ], [Snippets, Folders, Files]);

    const canEditRes = useCallback((res: ContextResource) => {
        const me = membersList.find(m => m.clientId === clientId);
        const isRoomOwner =
            roleRef === "owner" ||
            memberDetails?.role === "owner" ||
            roomDetails?.creatorClientId === clientId;

        if (isRoomOwner) return true;
        if (!me) return false;
        if (res.creatorId === clientId) return true;
        if (roleRef === "admin" || memberDetails?.role === "admin") {
            const creatorMember = membersList.find(m => m.clientId === res.creatorId);
            if (
                creatorMember?.role !== "admin" &&
                creatorMember?.role !== "owner"
            ) {
                return true;
            }
        }
        return false;
    }, [membersList, clientId, roleRef, memberDetails, roomDetails]);

    const canManageFolderPermission = useCallback((folder: Folder) => {
        if (!clientId) return false;

        const isRoomOwner =
            memberDetails?.role === "owner" ||
            roomDetails?.creatorClientId === clientId;

        if (isRoomOwner) return true;
        if (folder.creatorId === clientId) return true;
        if (memberDetails?.role !== "admin") return false;

        const folderCreator = membersList.find(m => m.clientId === folder.creatorId);
        return folderCreator?.role !== "owner" && folderCreator?.role !== "admin";
    }, [clientId, memberDetails, roomDetails, membersList]);

    // Shared with the workspace-wide drag-and-drop drop-target resolution
    // (shared/utils/permissions.ts) so this rule only lives in one place.
    const canCreateInFolder = (folder: Folder) => {
        return canCreateInFolderShared(folder, { clientId, yourRole, canAdd: canAdd() });
    };

    useEffect(() => {
        for (const r of resourcesList) {
            resCanEditRef.current[r.id] = canEditRes(r);
        }
    }, [resourcesList, canEditRes]);

    const [rightClickOptions, setRightClickOptions] = useState(false);
    const [rightClickData, setRightClickData] = useState<RightClickData | null>(null);

    const openRightClickMenu = (
        e: React.MouseEvent,
        target: RightClickTarget
    ) => {
        e.preventDefault();
        e.stopPropagation();

        setRightClickOptions(true);
        setRightClickData({
            x: e.clientX,
            y: e.clientY,
            target,
        });
    };

    const cancelDelClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        closeRightClick();
    }

    const adjustYforPop = (y: number) => {
        const viewportMargin = 8;
        return Math.max(viewportMargin, y);
    };

    const resourcesById = useMemo(() => {
        const map = new Map<string, ContextResource>();
        for (const r of resourcesList) map.set(r.id, r);
        return map;
    }, [resourcesList]);

    const filesAndSnipsRef = useRef<HTMLDivElement | null>(null);
    const dragScrollRafRef = useRef<number | null>(null);
    const dragScrollVelocityRef = useRef(0);

    const stopDragAutoScroll = useCallback(() => {
        if (dragScrollRafRef.current !== null) {
            cancelAnimationFrame(dragScrollRafRef.current);
            dragScrollRafRef.current = null;
        }
        dragScrollVelocityRef.current = 0;
    }, []);

    const startDragAutoScroll = useCallback(() => {
        if (dragScrollRafRef.current !== null) return;

        const tick = () => {
            const container = filesAndSnipsRef.current;

            if (!container) {
                dragScrollRafRef.current = null;
                return;
            }

            const velocity = dragScrollVelocityRef.current;

            if (velocity === 0) {
                dragScrollRafRef.current = null;
                return;
            }
            const prevScrollTop = container.scrollTop;
            container.scrollTop += velocity;
            if (container.scrollTop === prevScrollTop) {
                dragScrollVelocityRef.current = 0;
                dragScrollRafRef.current = null;
                return;
            }
            dragScrollRafRef.current = requestAnimationFrame(tick);
        };
        dragScrollRafRef.current = requestAnimationFrame(tick);
    }, []);

    const updateDragAutoScroll = useCallback((clientY: number) => {
        const container = filesAndSnipsRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const canScroll = container.scrollHeight > container.clientHeight;
        if (!canScroll) {
            stopDragAutoScroll();
            return;
        }
        const edgeThreshold = 70;
        const maxSpeed = 16;
        let nextVelocity = 0;

        const topZone = rect.top + edgeThreshold;
        const bottomZone = rect.bottom - edgeThreshold;

        if (clientY < topZone) {
            const intensity = Math.min(1, (topZone - clientY) / edgeThreshold);
            nextVelocity = -Math.max(4, Math.round(intensity * maxSpeed));
        } else if (clientY > bottomZone) {
            const intensity = Math.min(1, (clientY - bottomZone) / edgeThreshold);
            nextVelocity = Math.max(4, Math.round(intensity * maxSpeed));
        }

        dragScrollVelocityRef.current = nextVelocity;

        if (nextVelocity !== 0) {
            startDragAutoScroll();
        } else {
            stopDragAutoScroll();
        }
    }, [startDragAutoScroll, stopDragAutoScroll]);

    const rcRef = useRef<HTMLDivElement | null>(null);
    const rcPopupRef = useRef<HTMLDivElement | null>(null);
    const [popupPos, setPopupPos] = useState<{ forData: RightClickData; top: number; left: number } | null>(null);

    const getRightClickDisplayName = () => {
        if (!rightClickData) return;
        if (rightClickData.target.kind === 'folder' || rightClickData.target.kind === 'file') {
            return rightClickData.target.name;
        }

        if (rightClickData.target.kind === 'snippet') {
            let snip = Snippets.find(s => s._id === rightClickData.target.id);
            if (!snip) return;
            const ext = snip.snippetLanguage ? getSnippetExtension(snip.snippetLanguage) : 'txt'
            return snip.snippetName + "." + ext
        }



    }

    const renderRightClickPopup = () => {
        if (!rightClickOptions || !rightClickData) return null;

        const clickedResource =
            rightClickData.target.kind !== "blank"
                ? resourcesById.get(rightClickData.target.id)
                : null;

        const creator = clickedResource
            ? membersList?.find(m => m.clientId === clickedResource.creatorId)
            : null;

        const clickedFolder =
            rightClickData.target.kind === "folder"
                ? findFolderInTree(Folders, rightClickData.target.id) as Folder | null
                : null;

        const estimatedLeft = Math.max(
            8,
            Math.min(
                rightClickData.x - 50,
                window.innerWidth - (deletePopup ? 336 : 242)
            )
        );
        const estimatedTop = adjustYforPop(rightClickData.y);

        const measured = popupPos?.forData === rightClickData ? popupPos : null;
        const left = measured?.left ?? estimatedLeft;
        const top = measured?.top ?? estimatedTop;

        return createPortal(
            <div ref={rcRef} className="right-click-layer" onClick={(e) => e.stopPropagation()}>
                <div
                    ref={rcPopupRef}
                    className={`right-click-popup ${deletePopup ? "delete-confirm-open" : ""} ${ctheme === "light" ? "light" : "dark"}`}
                    style={{
                        position: "fixed",
                        left,
                        top,
                        zIndex: 9999,
                    }}
                >

                    <div className='relative'>
                        {deletePopup &&
                            <div className='sb-rc-delete-popup'>

                                <h3>Are you sure you would like to delete this item?</h3>
                                <p>This action cannot be undone</p>

                                <div className='delete-pop-btns'>
                                    <div onClick={(e) => rcDeleteClick(e)} className='delete-pop-btn dp-y-s'>Yes</div>
                                    <div onClick={(e) => cancelDelClick(e)} className='delete-pop-btn dp-c-s'>Cancel</div>
                                </div>

                            </div>
                        }
                        <div className={styles.rcHeader}>
                            <div className="rc-title">
                                {getRightClickDisplayName()}

                            </div>
                            <div className={styles.rcSub}>
                                {
                                    rightClickData.target.kind === 'snippet' || rightClickData.target.kind === 'folder' ? ' Created by: ' : 'Uploaded by: '
                                }

                                <span className={styles.rcCreator}>
                                    {creator?.clientId === clientId ? 'you' : creator?.name ?? "Unknown"}
                                </span>
                            </div>
                        </div>

                        <div className={styles.rcDivider} />



                        {rightClickData.target.kind === "folder" && clickedFolder && canCreateInFolder(clickedFolder) && (
                            <div className="rc-section">
                                <button onClick={() => rcAddIntoFolder('file')} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                    <div className="rc-icon">
                                        <AddFileIcon />
                                    </div>
                                    <span className="rc-label">Add File</span>
                                </button>

                                <button onClick={() => rcAddIntoFolder('folder')} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                    <div className="rc-icon">
                                        <NewFolderIcon />
                                    </div>
                                    <span className="rc-label">Add Folder</span>
                                </button>
                            </div>
                        )}

                        {clickedResource && canEditRes(clickedResource) && (
                            <div className="rc-section">
                                <div className={styles.rcSectionTitle}>Manage</div>



                                {rightClickData.target && rightClickData.target.kind != 'file' ? (
                                    <button onClick={rcRename} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                        <div className="rc-icon">
                                            <RenameIcon />
                                        </div>
                                        <span className="rc-label">Rename</span>

                                    </button>
                                ) : (
                                    <></>
                                )}


                                <button onClick={rcDelete} type="button" className="clickable rc-popup-btn quick-flex rc-btn danger">
                                    <div className="rc-icon">
                                        <TrashIcon />
                                    </div>
                                    <span onClick={rcDelete} className="rc-label">Delete</span >

                                </button>

                                {rightClickData.target.kind === "folder" && clickedFolder && canManageFolderPermission(clickedFolder) && (
                                    <div className="rc-folder-permission">
                                        <div className="rc-folder-permission-label">Folder Access</div>
                                        <div className="rc-folder-permission-options">
                                            {(["restricted", "open"] as const).map((option) => {
                                                const active = normalizeFolderPermission(clickedFolder) === option;

                                                return (
                                                    <button
                                                        key={option}
                                                        type="button"
                                                        className={`rc-folder-permission-option ${active ? "active" : ""}`}
                                                        onClick={() => {
                                                            if (!active) void rcChangeFolderPermission(option);
                                                        }}
                                                    >
                                                        {option === "restricted" ? "Restricted" : "Open"}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="rc-section">

                            <button onClick={(e) => rcDownload(e)} type="button" className="clickable rc-popup-btn quick-flex rc-btn">
                                <div className="rc-icon">
                                    <Download />
                                </div>
                                <span className="rc-label">Download</span>

                            </button>
                        </div>

                        {rightClickData.target.kind === "blank" && (
                            <>
                                <div className={styles.rcDivider} />
                                <div className="rc-section">
                                    <div className={styles.rcSectionTitle}>Workspace</div>

                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };


    const flattenFolders = (folders: Folder[]): Folder[] => {
        const result: Folder[] = [];

        const walk = (items: Folder[]) => {
            for (const folder of items) {
                result.push(folder);
                walk(folder.folders ?? []);
            }
        };

        walk(folders);
        return result;
    };



    const getFolderDepth = (fId: string) => {

        const allFolders = flattenFolders(Folders)
        const targetFolder = allFolders.find(f => f._id === fId);

        let curr = targetFolder;
        if (!curr) return;
        let depthCount = 1;

        while (curr?.folderParent != null) {
            curr = allFolders.find(f => f._id === curr?.folderParent);
            depthCount += 1;
        }
        return depthCount;

    }





    useEffect(() => {
        if (!rightClickOptions) return;
        const close = () => {
            closeRightClick()
        };
        const onMouseDown = (e: MouseEvent) => {
            const el = rcRef.current;
            if (!el) return close();
            if (el.contains(e.target as Node)) return;
            close();
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };

        document.addEventListener("mousedown", onMouseDown, true);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("mousedown", onMouseDown, true);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [rightClickOptions]);


    const buildFolderDownloadObj = (folderId: string): FolderObj => {
        const folder = Folders.find((f) => f._id === folderId);
        if (!folder) {
            return { snips: [], files: [], folds: [] };
        }

        const selectFiles: string[] = [];
        const selectSnippets: string[] = [];
        const selectFolders: string[] = [];

        const folderSnips = folder.snippets;
        if (folderSnips) {
            folderSnips.forEach(snip => {
                selectSnippets.push(snip);
            })
        }
        const folderFiles = folder.fileRefs;
        if (folderFiles) {
            folderFiles.forEach(fil => {
                selectFiles.push(fil);
            })
        }
        const subFolders = folder.folders;
        if (subFolders) {
            subFolders.forEach(sf => {
                selectFolders.push(sf._id);
                buildFolderDownloadObj(sf._id);
            })
        }

        return {
            snips: selectSnippets,
            files: selectFiles,
            folds: selectFolders,
        }

    }

    const rcDownload = async (e: React.MouseEvent) => {
        try {
            e.stopPropagation();
            e.preventDefault();
            let fileTargetId: string;
            if (rightClickData?.target.kind === 'snippet') {
                fileTargetId = rightClickData.target.id;
                const snip = Snippets.find((s) => s._id === fileTargetId)
                if (!snip) return
                let mime = '';
                let ext = '';
                if (snip.snippetType === 'text') {
                    mime = "text/plain;charset=utf-8"
                    ext = 'txt'
                } else {
                    const lang = snip.snippetLanguage ? snip.snippetLanguage : 'text';
                    mime = normalizeMime(lang);
                    ext = getSnippetExtension(lang)

                }
                await downloadSnippet(`${snip.snippetName}.${ext}`, snip.snipText, mime)
            } else if (rightClickData?.target.kind === 'file') {
                fileTargetId = rightClickData.target.id;
                const file = Files.find((f) => f._id === fileTargetId);
                if (!file) return
                if (!roomId) return
                await downloadFile(roomId, fileTargetId, file.name);
            } else if (rightClickData?.target.kind === 'folder') {
                if (!roomId) return;

                const folderObj = findFolderInTree(Folders, rightClickData.target.id);
                if (!folderObj) return;

                const subtree = collectFolderSubtreeForSelection(folderObj);

                const files = subtree.filter((fi) => fi.resType === 'file');
                const snippets = subtree.filter((s) => s.resType === 'snippet');
                const folders = subtree.filter((f) => f.resType === 'folder');

                const fileIds = files.map(fi => fi.id);
                const snippetIds = snippets.map(sn => sn.id);
                const folderIds = folders.map(fo => fo.id);

                await downloadMultiple(roomId, snippetIds, fileIds, folderIds, {
                    name: folderObj.fname,
                    itemType: "folder",
                });
            }


            // else {
            //     if (!roomId) return
            //     if (!rightClickData) return
            //     fileTargetId = rightClickData?.target.id;

            //     const folderObject = buildFolderDownloadObj(fileTargetId);

            //     downloadMultiple(roomId, folderObject.snips, folderObject.files, folderObject.folds)
            // }

            closeRightClick();

        } catch (err) {
            setDownloadFileErrorPopup(true);
            closeRightClick();
        }

    }


    const [rename, setRename] = useState(false);
    const [nameChangeData, setNameChangeData] = useState<renameObj | null>();
    const [nameDraft, setNameDraft] = useState<string>("");
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [renameError, setRenameError] = useState<string | null>(null);


    const closeRightClick = () => {
        setRightClickOptions(false);
        setRightClickData(null);
        setDeltePopup(false);
    };

    const rcRename = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!rightClickData) return;
        if (rightClickData.target.kind === "blank") return;
        closeRightClick();
        const currentName = rightClickData.target.name ?? "";
        setRename(true);
        setNameChangeData({
            name: currentName,
            rType: rightClickData.target.kind,
            id: rightClickData.target.id,
        });
        setNameDraft(currentName);
    };

    const rcAddIntoFolder = (type: 'file' | 'folder') => {
        if (!rightClickData || rightClickData.target.kind !== "folder") return;
        const folderId = rightClickData.target.id;
        closeRightClick();
        setActiveFolderId(folderId);
        startInlineCreate(type, folderId);
    };

    const rcChangeFolderPermission = async (folderPermission: "restricted" | "open") => {
        if (!rightClickData || rightClickData.target.kind !== "folder") return;

        try {
            await api.patch(`/rooms/${roomId}/folders/${rightClickData.target.id}/permission`, {
                folderPermission,
            });
        } catch (err: any) {
            const status = err?.response?.status;
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "error updating folder permissions";
            setLimitMessage(status ? `${status}: ${message}` : message);
            setLimitPopup(true);
        }
    };

    const cancelRename = () => {
        setRename(false);
        setNameChangeData(null);
        setNameDraft("");
        setRenameError(null);
    };

    const commitRename = async (nextValue: string) => {
        if (!nameChangeData) return;

        const next = nextValue.trim();
        if (!next) return cancelRename();

        if (next === nameChangeData.name) return cancelRename();
        try {
            setIsRenaming(true);
            setRenameError(null);
            if (nameChangeData.rType === 'snippet') {
                await api.patch(`/snippets/updatesnippet/${roomId}/${nameChangeData.id}`, {
                    snippetName: next,
                })
            } else if (nameChangeData.rType === 'folder') {
                await api.patch(`/rooms/${roomId}/folders/${nameChangeData.id}/rename`, {
                    fName: next,
                });
            } else {
                setRenameError("Files cannot be renamed here.");
                return;
            }
            cancelRename();
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.response?.data?.message || "Rename failed. Try again.";
            setRenameError(message);
            setLimitMessage(message);
            setLimitPopup(true);
        } finally {
            setIsRenaming(false);
        }
    };





    const [deletePopup, setDeltePopup] = useState(false);

    // Measures the actual rendered popup (not the zero-size portal layer it
    // sits in) and clamps it to the viewport on both axes. Stored in state
    // (keyed to the rightClickData that produced it) rather than mutated
    // directly on the DOM node, since a plain style mutation gets wiped out
    // by React's next re-render — and in a live-collab app, socket-driven
    // state updates happen constantly while a menu is open.
    useLayoutEffect(() => {
        if (!rightClickOptions || !rightClickData) return;

        const popup = rcPopupRef.current;
        if (!popup) return;

        const viewportMargin = 8;
        const rect = popup.getBoundingClientRect();

        let nextTop = rect.top;
        const overflowBottom = rect.bottom - (window.innerHeight - viewportMargin);
        if (overflowBottom > 0) nextTop -= overflowBottom;
        if (nextTop < viewportMargin) nextTop = viewportMargin;

        let nextLeft = rect.left;
        const overflowRight = rect.right - (window.innerWidth - viewportMargin);
        if (overflowRight > 0) nextLeft -= overflowRight;
        if (nextLeft < viewportMargin) nextLeft = viewportMargin;

        setPopupPos(prev => {
            if (prev?.forData === rightClickData && prev.top === nextTop && prev.left === nextLeft) {
                return prev;
            }
            return { forData: rightClickData, top: nextTop, left: nextLeft };
        });
    }, [rightClickOptions, rightClickData, deletePopup]);

    const rcDelete = () => {
        setDeltePopup(true);
    }

    const rcDeleteClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!rightClickData) return;
        try {

            const resType = rightClickData.target.kind;
            const resId = rightClickData.target.id;

            if (resType === 'snippet') {
                await api.delete(`/snippets/${resId}/room/${roomId}`);
            } else if (resType === 'file') {
                await api.delete(`/rooms/${roomId}/${resId}/files`);
            } else if (resType === 'folder') {
                await api.delete(`/rooms/${roomId}/folders/${resId}/delete`)
            } else {
                setDeltePopup(false);
                closeRightClick();
                return;
            }

            setDeltePopup(false);
            closeRightClick();
        } catch (error) {
            setLimitMessage('Could not delete item. Please try again.');
            setLimitPopup(true);
        }
    }


    const downloadSelectedClick = async () => {

        try {

            if (selectedIds.length === 1 && selectedIds[0].resType === 'snippet') {
                const selectedSnip = Snippets.find((s) => s._id === selectedIds[0].id)
                if (!selectedSnip) return
                let mime = '';
                let ext = '';

                if (selectedSnip.snippetType === 'text') {
                    mime = "text/plain;charset=utf-8"
                    ext = 'txt'
                } else {
                    const lang = selectedSnip.snippetLanguage;
                    if (!lang) return;

                    mime = normalizeMime(lang);

                    const maybeExt = getSnippetExtension(lang);
                    ext = maybeExt ?? "txt";
                }

                await downloadSnippet(`${selectedSnip.snippetName}.${ext}`, selectedSnip.snipText, mime)

            } else if (selectedIds.length === 1 && selectedIds[0].resType === 'file') {
                const selectedFile = Files.find((f) => f._id === selectedIds[0].id);

                if (!selectedFile) return
                if (!roomId) return

                await downloadFile(roomId, selectedFile._id, selectedFile.name);
            } else if (!selectedIds.length) {
                setDownloadFileErrorPopup(true);
            } else {

                const files = selectedIds.filter((fi) => fi.resType === 'file')
                const snippets = selectedIds.filter((s) => s.resType === 'snippet');
                const folders = selectedIds.filter((f) => f.resType === 'folder')


                const fileIds = files.map(fi => fi.id);
                const snippetIds = snippets.map(sn => sn.id);
                const folderIds = folders.map(fo => fo.id);


                if (!roomId) return

                const selectedFolder =
                    selectedIds.length === 1 && selectedIds[0].resType === 'folder'
                        ? resourcesById.get(selectedIds[0].id)
                        : null;

                await downloadMultiple(roomId, snippetIds, fileIds, folderIds, {
                    name: selectedFolder?.name || `${selectedIds.length} selected items`,
                    itemType: selectedFolder?.kind === 'folder' ? "folder" : "file",
                })

            }


            setSelectMode(false);
            setSelectedIds([]);


        } catch (error) {
            setDownloadFileErrorPopup(true);
        }
    }

    // Delete permission mirrors edit permission everywhere else in this file
    // (canEditRes already gates the right-click Delete button) — a folder's
    // own creator/permissions decide it, its contents are never inspected.
    const canDeleteSelected = (item: selectRes) => {
        const res = resourcesById.get(item.id);
        return !!res && canEditRes(res);
    };

    const deletableSelectedIds = selectedIds.filter(canDeleteSelected);

    const bulkDeleteClick = () => {
        if (!deletableSelectedIds.length) return;
        setConfirmBulkDelete(true);
    };

    const confirmBulkDeleteClick = async () => {
        if (!roomId) return;
        const toDelete = deletableSelectedIds;
        const blockedCount = selectedIds.length - toDelete.length;

        const { deletedIds, failedIds } = await deleteResources(roomId, toDelete);

        setSelectedIds(prev => prev.filter(s => !deletedIds.includes(s.id)));
        setConfirmBulkDelete(false);

        if (blockedCount > 0 || failedIds.length > 0) {
            if (deletedIds.length > 0) {
                setLimitMessage('Some selected items could not be deleted because you do not have permission.');
            } else {
                setLimitMessage('Could not delete item. Please try again.');
            }
            setLimitPopup(true);
        }
    };


    const closeSelectModeClick = () => {
        setSelectMode(false)
        setSelectedIds([]);
        setConfirmBulkDelete(false);

    }

    // Once the user has actually selected something, returning to zero
    // selected (by unchecking everything) exits select mode automatically.
    // Opening select mode with nothing selected yet must NOT trigger this.
    useEffect(() => {
        if (!selectMode) {
            hadSelectionRef.current = false;
            return;
        }

        if (selectedIds.length > 0) {
            hadSelectionRef.current = true;
        } else if (hadSelectionRef.current) {
            setSelectMode(false);
            setConfirmBulkDelete(false);
            hadSelectionRef.current = false;
        }
    }, [selectMode, selectedIds]);

    const checkClick = (
        e: React.MouseEvent<HTMLDivElement>,
        id: string,
        resType: string
    ) => {
        e.stopPropagation();
        setSelectMode(true);

        setSelectedIds(prev => {
            const exists = prev.some(s => s.id === id);
            if (exists) return prev.filter(s => s.id !== id);
            return [...prev, { id, resType }];
        });
    };

    const isChecked = (id: string) => {
        if (selectedIds.some((s) => s.id === id)) {
            return true
        }
        return false
    }




    const selectAllClick = () => {
        const folderIds = collectFolderIdsFromTree(Folders).map(
            (id): selectRes => ({ id, resType: "folder" })
        );

        const snippetIds: selectRes[] = Snippets.map((s) => ({ id: String(s._id), resType: "snippet" }));
        const fileIds: selectRes[] = Files.map((fi) => ({ id: String(fi._id), resType: "file" }));

        setSelectedIds([...folderIds, ...snippetIds, ...fileIds]);
    };



    const folderSelectClick = (
        e: React.MouseEvent<HTMLDivElement>,
        folderId: string
    ) => {
        e.stopPropagation();
        setSelectMode(true);

        setSelectedIds((prev) => {
            const isSelected = prev.some(
                (x) => x.id === String(folderId) && x.resType === "folder"
            );

            const folderObj = findFolderInTree(Folders, folderId);
            if (!folderObj) return prev;

            const subtree = collectFolderSubtreeForSelection(folderObj);

            if (!isSelected) {
                // add subtree
                const existing = new Set(prev.map(x => `${x.resType}:${x.id}`));
                const merged = [...prev];

                for (const item of subtree) {
                    const key = `${item.resType}:${item.id}`;
                    if (!existing.has(key)) merged.push(item);
                }

                return merged;
            } else {
                // remove subtree
                const remove = new Set(subtree.map(x => `${x.resType}:${x.id}`));
                return prev.filter(x => !remove.has(`${x.resType}:${x.id}`));
            }
        });
    };


    const snippetById = React.useMemo(
        () => new Map<string, Snippet>(Snippets.map(s => [String(s._id), s])),
        [Snippets]
    );





    const dragGhostCleanupRef = useRef<null | (() => void)>(null);


    useEffect(() => {
        setOpenMap(prev => {
            // if we've already set something (user clicked), don't override
            if (Object.keys(prev).length > 0) return prev;

            const next: Record<string, boolean> = {};

            Folders.forEach(folder => {
                if ((folder.snippets?.length ?? 0) > 0) {
                    next[folder._id] = true;   // open folders that have snippets
                }
            });

            return next;
        });
    }, [Folders]);




    const [draggedItem, setDraggedItem] = useState<DragPayload | null>(null);
    const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

    // if you already have this ref, keep it. otherwise:


    /** A safe wrapper around makeDragGhost so this file compiles even if it's not present yet */
    function safeMakeDragGhost(
        nativeEvent: DragEvent,
        ghost: { label?: string; iconSrc?: string }
    ) {
        try {
            // @ts-expect-error - makeDragGhost is your util; if missing, we just skip ghost
            return makeDragGhost(nativeEvent, ghost);
        } catch {
            return null;
        }
    }

    const onDragEnd = () => {
        stopDragAutoScroll();

        if (dragGhostCleanupRef.current) {
            dragGhostCleanupRef.current();
            dragGhostCleanupRef.current = null;
        }
        setDraggedItem(null);
        setDragOverFolderId(null);
    };
    // ---------- DRAG STARTS ----------


    useEffect(() => {
        return () => {
            stopDragAutoScroll();
        };
    }, [stopDragAutoScroll]);


    const canDragSnip = (snippetId: string, clientId: string) => {
        const snip = Snippets.find((s) => s._id === snippetId);
        if (!snip) return false;
        if (yourRole === "owner") return true;
        if (snip.createdBy === clientId) return true;
        if (memberDetails?.canCreate !== true && memberDetails?.role === 'member') return false;
        if (yourRole !== "admin") return false;
        const creatorMember = membersList.find((m) => m.clientId === snip.createdBy);
        const creatorRole = creatorMember?.role;
        if (creatorRole === "owner" || creatorRole === "admin") return false;
        return true;
    };

    const canDragFile = (fileId: string, clientId: string) => {
        const file = Files.find((f) => String(f._id) === String(fileId));
        if (!file) return false;
        if (yourRole === "owner") return true;
        if (file.uploaderClientId === clientId) return true;
        if (memberDetails?.canCreate !== true && memberDetails?.role === 'member') return false;
        if (yourRole !== "admin") return false;

        const creatorMember = membersList.find(
            (m) => m.clientId === file.uploaderClientId
        );

        if (creatorMember?.role === "owner" || creatorMember?.role === "admin") return false;

        return true;
    };

    const canDragFolder = (folderId: string, clientId: string) => {
        const folder = findFolderInTree(Folders, folderId);
        if (!folder) return false;
        if (yourRole === "owner") return true;
        if (folder.creatorId === clientId) return true;
        if (memberDetails?.canCreate !== true && memberDetails?.role === 'member') return false;
        if (yourRole !== "admin") return false;
        const creatorMember = membersList.find((m) => m.clientId === folder.creatorId);
        if (creatorMember?.role === "owner" || creatorMember?.role === "admin") return false;

        return true;
    };



    const onSnippetDragStart = (e: React.DragEvent<HTMLDivElement>, snippet: Snippet) => {


        const cliId = clientId

        if (!cliId) return

        if (!canDragSnip(snippet._id, cliId)) return

        if (dragGhostCleanupRef.current) {
            dragGhostCleanupRef.current();
            dragGhostCleanupRef.current = null;
        }



        const iconSrc =
            `${process.env.PUBLIC_URL}/images/` +
            `${getSnippetIcon(snippet.snippetLanguage || "txt")}.png`;

        const label =
            `${snippet.snippetName}.` +
            `${getSnippetExtension(snippet.snippetLanguage || "txt")}`;

        const payload: DragPayload = { kind: "snippet", id: snippet._id };

        setDraggedItem(payload);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-flashroom-dnd", JSON.stringify(payload));

        const cleanup = safeMakeDragGhost(e.nativeEvent as unknown as DragEvent, { label, iconSrc });
        if (cleanup) dragGhostCleanupRef.current = cleanup;
    };

    const onFileDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        file: { _id: string; name: string }
    ) => {

        const cliId = clientId

        if (!cliId) return

        if (!canDragFile(file._id, cliId)) return

        if (dragGhostCleanupRef.current) {
            dragGhostCleanupRef.current();
            dragGhostCleanupRef.current = null;
        }


        const label = file.name;
        const iconSrc = `${process.env.PUBLIC_URL}/images/file.png`;

        const payload: DragPayload = { kind: "file", id: file._id };

        setDraggedItem(payload);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-flashroom-dnd", JSON.stringify(payload));

        const cleanup = safeMakeDragGhost(e.nativeEvent as unknown as DragEvent, { label, iconSrc });
        if (cleanup) dragGhostCleanupRef.current = cleanup;
    };

    const onFolderDragStart = (
        e: React.DragEvent<HTMLDivElement>,
        folder: { _id: string; fname: string }
    ) => {



        const cliId = clientId

        if (!cliId) return

        if (!canDragFolder(folder._id, cliId)) return



        if (dragGhostCleanupRef.current) {
            dragGhostCleanupRef.current();
            dragGhostCleanupRef.current = null;
        }




        const label = folder.fname;
        const iconSrc = `${process.env.PUBLIC_URL}/images/folder.png`;

        const payload: DragPayload = { kind: "folder", id: folder._id };

        setDraggedItem(payload);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-flashroom-dnd", JSON.stringify(payload));

        const cleanup = safeMakeDragGhost(e.nativeEvent as unknown as DragEvent, { label, iconSrc });
        if (cleanup) dragGhostCleanupRef.current = cleanup;
    };





    function isWithinFolderTop(e: React.DragEvent<HTMLDivElement>) {
        return (e.target as HTMLElement | null)?.closest?.(`.${styles.folderTop}`) === e.currentTarget;
    }


    // Find a folder object anywhere in the rendered tree
    const findFolderInTree = (roots: any[], id: string): any | null => {
        const stack = [...roots];
        while (stack.length) {
            const f = stack.pop();
            if (!f?._id) continue;
            if (String(f._id) === String(id)) return f;

            if (Array.isArray(f.folders)) {
                for (const child of f.folders) {
                    if (child && typeof child === "object") stack.push(child);
                }
            }
        }
        return null;
    };

    // Returns true if `targetId` is inside `sourceId`'s subtree (meaning source -> ... -> target)
    const isDescendantFolder = (roots: any[], sourceId: string, targetId: string): boolean => {
        const source = findFolderInTree(roots, sourceId);
        if (!source) return false;

        const stack = [...(source.folders ?? [])];
        while (stack.length) {
            const f = stack.pop();
            if (!f?._id) continue;
            if (String(f._id) === String(targetId)) return true;

            if (Array.isArray(f.folders)) {
                for (const child of f.folders) {
                    if (child && typeof child === "object") stack.push(child);
                }
            }
        }
        return false;
    };

    const canDropIntoFolder = (folderId: string, item: DragPayload | null) => {
        if (!item || !clientId) return false;

        const targetFolder = findFolderInTree(Folders, folderId);
        if (!targetFolder) return false;

        if (item.kind === "snippet" && !canDragSnip(item.id, clientId)) return false;
        if (item.kind === "file" && !canDragFile(item.id, clientId)) return false;
        if (item.kind === "folder" && !canDragFolder(item.id, clientId)) return false;

        if (yourRole === "owner" || yourRole === "admin") return true;
        if (targetFolder.creatorId === clientId) return true;

        return normalizeFolderPermission(targetFolder) === "open";
    };






    const moveSnippetToFolder = async (snippetId: string, folderId: string) => {
        await api.patch(`/rooms/${roomId}/folders/${folderId}/addsnippet`, { snippetId });
    };


    const moveFileToFolder = async (fileId: string, folderId: string) => {
        await api.patch(`/rooms/${roomId}/folders/${folderId}/files`, { fileId });
    };

    const moveFolderToFolder = async (folderId: string, parentFolderId: string) => {
        await api.patch(`/rooms/${roomId}/folders/${parentFolderId}/addFolder`, { folderId });
    };


    const moveSnippetToMain = async (snippetId: string) => {
        await api.patch(`/rooms/${roomId}/snippets/${snippetId}/move`)
    }


    const moveFileToMain = async (fileId: string) => {
        await api.patch(`/rooms/${roomId}/files/${fileId}/move`)
    }

    const moveFolderToMain = async (folderId: string) => {
        await api.patch(`/rooms/${roomId}/folders/${folderId}/move`)
    }

    // ---------- DROP ----------

    const handleMainDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        let item = draggedItem;
        if (!item) {
            const raw = e.dataTransfer.getData("application/x-flashroom-dnd");
            if (raw) {
                try {
                    item = JSON.parse(raw) as DragPayload;
                } catch {
                    item = null;
                }
            }
        }
        if (!item) return;

        setDragOverFolderId(null);

        try {
            if (item.kind === "snippet") await moveSnippetToMain(item.id);
            else if (item.kind === "file") await moveFileToMain(item.id);
            else await moveFolderToMain(item.id);
        } finally {
            setDraggedItem(null);
        }
    };

    const handleFolderDrop = async (e: React.DragEvent<HTMLDivElement>, targetFolderId: string) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isWithinFolderTop(e)) return; // ✅



        let item = draggedItem;
        if (!item) {
            const raw = e.dataTransfer.getData("application/x-flashroom-dnd");
            if (raw) {
                try {
                    item = JSON.parse(raw) as DragPayload;
                } catch {
                    item = null;
                }
            }
        }
        if (!item) return;

        setDragOverFolderId(null);

        if (!canDropIntoFolder(targetFolderId, item)) return;

        try {
            if (item.kind === "snippet") await moveSnippetToFolder(item.id, targetFolderId);
            else if (item.kind === "file") await moveFileToFolder(item.id, targetFolderId);
            else {
                // folder -> folder guards
                const depth = getFolderDepth(targetFolderId);

                if (depth && depth >= 5) {
                    setLimitPopup(true);
                    setLimitMessage("Maximum folder depth of 5 cannot be exceeded")
                    return;
                }

                if (String(item.id) === String(targetFolderId)) {
                    // dropping into itself
                    return;
                }

                if (isDescendantFolder(Folders, item.id, targetFolderId)) {
                    // dropping into one of its own children/grandchildren
                    return;
                }

                await moveFolderToFolder(item.id, targetFolderId);
            }
        } finally {
            setDraggedItem(null);
        }

    };


    function canAdd() {
        return canAddResource(memberDetails);
    }


    const yourRole = useMemo(() => {
        const isAdmin = memberDetails?.role === 'admin';
        const isOwner = memberDetails?.role === 'owner' || memberDetails?.clientId === roomDetails?.creatorClientId;
        if (isOwner) return 'owner';
        if (isAdmin) return 'admin';
        return 'member';
    }, [memberDetails, roomDetails, clientId]);

    useEffect(() => {
        setRoleRef(yourRole);
    }, [yourRole]);



    const newSnipClick = () => {
        if (!hasOwnerBypass && Snippets.length + Files.length >= MAX_ROOM_ITEMS) {
            setLimitPopup(true);
            setLimitMessage('You have reached the maximum number of files. You must delete a file to create a new one.');
        } else {
            startInlineCreate('file', activeFolderId);
        }
    }

    const newFileClick = () => {
        if (!hasOwnerBypass && Snippets.length + Files.length >= MAX_ROOM_ITEMS) {
            setLimitPopup(true);
            setLimitMessage('You have reached the maximum number of files. You must delete a file to create a new one.')
        } else {
            if (globalNewSnipPopup) {
                setGlobalNewSnipPopup(false);
            }
            setGlobalNewFilePopup(true);
        }
    };


    const newFolderClick = () => {
        if (!hasOwnerBypass && Folders.length >= FOLDER_LIMIT) {
            setLimitPopup(true);
            setLimitMessage('You have reached the maximum number of folders. You must delete a folder to create a new one.')
        } else {
            startInlineCreate('folder', activeFolderId);
        }
    }

    const startInlineCreate = (type: 'file' | 'folder', parentId: string | null) => {
        if (parentId) {
            setOpenMap(prev => ({ ...prev, [parentId]: true }));
        }
        setInlineCreate({ type, parentId });
        setInlineCreateName('');
        setInlineCreateError(null);
    };

    const cancelInlineCreate = () => {
        setInlineCreate(null);
        setInlineCreateName('');
        setInlineCreateError(null);
        setInlineCreateBusy(false);
    };

    // The typed value collapsed into the effective full name to check for a
    // collision — folders compare as-is, files only once a "name.ext"-shaped
    // suffix exists (comparing against whatever was actually typed, not
    // waiting on it to be a *valid* extension — that's a separate, submit-only
    // check via resolveSnippetFromTypedName).
    const getLiveEffectiveName = (type: 'file' | 'folder', rawValue: string): string | null => {
        const trimmed = rawValue.trim();
        if (!trimmed) return null;

        if (type === 'folder') return trimmed;

        const dot = trimmed.lastIndexOf('.');
        return dot > 0 ? trimmed : null;
    };

    const checkLiveCollision = (type: 'file' | 'folder', parentId: string | null, rawValue: string): boolean => {
        const effectiveName = getLiveEffectiveName(type, rawValue);
        if (!effectiveName) return false;

        return isNameTakenInDirectory({
            folderParent: parentId,
            effectiveName,
            folders: Folders,
            snippets: Snippets,
            files: Files,
        });
    };

    const getInlineCreateValidationError = (type: 'file' | 'folder', rawValue: string): string | null => {
        const trimmed = rawValue.trim();
        if (!trimmed) return type === 'folder' ? 'Folder name required' : 'File name required';
        if (type === 'folder') return null;

        const resolved = resolveSnippetFromTypedName(trimmed);
        if (resolved.ok) return null;

        return resolved.reason === 'language' ? 'Choose a valid language' : 'Use format: name.extension';
    };

    const submitInlineCreate = async () => {
        if (!inlineCreate || inlineCreateBusy) return;

        const trimmed = inlineCreateName.trim();
        const validationError = getInlineCreateValidationError(inlineCreate.type, trimmed);

        if (validationError) {
            setInlineCreateError(validationError);
            return;
        }

        let resolvedSnippet: Extract<ReturnType<typeof resolveSnippetFromTypedName>, { ok: true }> | null = null;

        if (inlineCreate.type === 'file') {
            const resolved = resolveSnippetFromTypedName(trimmed);
            if (!resolved.ok) {
                setInlineCreateError(
                    resolved.reason === 'language' ? 'Choose a valid language' : 'Use format: name.extension'
                );
                return;
            }

            resolvedSnippet = resolved;
        }

        if (checkLiveCollision(inlineCreate.type, inlineCreate.parentId, trimmed)) {
            setInlineCreateError(NAME_TAKEN_MESSAGE);
            return;
        }

        try {
            setInlineCreateBusy(true);
            setInlineCreateError(null);

            if (inlineCreate.type === 'folder') {
                await api.post(`/rooms/${roomId}/folder`, {
                    fname: trimmed,
                    creatorId: clientId,
                    folderPermission: 'restricted',
                    folderParent: inlineCreate.parentId,
                });
            } else {
                const res = await api.post(`/rooms/${roomId}/snippets`, {
                    snippetName: resolvedSnippet!.baseName,
                    snippetType: resolvedSnippet!.snippetType,
                    snipText: '',
                    snippetLanguage: resolvedSnippet!.snippetLanguage,
                    snipPermissions: 'view',
                    createdBy: clientId,
                    snippetSetting: 'room',
                    folderParent: inlineCreate.parentId,
                });

                const newId = res.data?.snippet?._id;
                if (newId) setPendingOpenId(newId);
            }

            cancelInlineCreate();
        } catch (err: any) {
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                'Could not create item. Please try again.';
            setInlineCreateError(message);
        } finally {
            setInlineCreateBusy(false);
        }
    };

    const inlineCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void submitInlineCreate();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelInlineCreate();
        }
    };

    useEffect(() => {
        if (!inlineCreate || inlineCreateBusy) return;

        const handleInlineCreatePointerDown = (event: PointerEvent) => {
            const target = event.target;
            if (
                inlineCreateRef.current &&
                target instanceof Node &&
                !inlineCreateRef.current.contains(target)
            ) {
                cancelInlineCreate();
            }
        };

        document.addEventListener("pointerdown", handleInlineCreatePointerDown, true);

        return () => {
            document.removeEventListener("pointerdown", handleInlineCreatePointerDown, true);
        };
    }, [inlineCreate, inlineCreateBusy]);

    useEffect(() => {
        if (inlineCreate) {
            requestAnimationFrame(() => inlineCreateInputRef.current?.focus());
        }
    }, [inlineCreate]);

    // Live type-icon preview while typing a new file's name — resolves the
    // same way submitInlineCreate does, so the icon shown here always
    // matches what the file would actually be created as (mirrors VS Code's
    // inline new-file icon updating as you type the extension).
    const inlineCreatePreviewIcon = useMemo(() => {
        if (!inlineCreate || inlineCreate.type !== 'file') return null;

        const resolved = resolveSnippetFromTypedName(inlineCreateName);
        if (!resolved.ok) return null;

        return getSnippetIcon(resolved.snippetType === 'code' ? (resolved.snippetLanguage ?? 'txt') : 'txt');
    }, [inlineCreate, inlineCreateName]);

    const renderInlineCreateRow = (parentId: string | null, depth = 0) => {
        if (!inlineCreate || inlineCreate.parentId !== parentId) return null;

        return (
            <div ref={inlineCreateRef} className={styles.inlineCreateWrap} style={{ paddingLeft: 10 + depth * 12 }}>
                <div className={`${styles.resSnipp} ${styles.inlineCreateRow}`}>
                    {inlineCreate.type === 'folder' ? (
                        <FolderIconEditor />
                    ) : inlineCreatePreviewIcon ? (
                        <img
                            draggable={false}
                            className={styles.inlineCreateTypeIcon}
                            src={`${process.env.PUBLIC_URL}/images/${inlineCreatePreviewIcon}.png`}
                            alt=""
                        />
                    ) : (
                        <FileIconEditor />
                    )}
                    <input
                        ref={inlineCreateInputRef}
                        className={styles.inlineCreateInput}
                        type="text"
                        maxLength={40}
                        placeholder={inlineCreate.type === 'folder' ? 'folder name' : 'file name'}
                        value={inlineCreateName}
                        disabled={inlineCreateBusy}
                        onChange={(e) => {
                            const value = e.target.value;
                            setInlineCreateName(value);

                            if (checkLiveCollision(inlineCreate.type, inlineCreate.parentId, value)) {
                                setInlineCreateError(NAME_TAKEN_MESSAGE);
                            } else if (inlineCreateError === NAME_TAKEN_MESSAGE) {
                                setInlineCreateError(null);
                            }
                        }}
                        onKeyDown={inlineCreateKeyDown}
                    />
                </div>
                {inlineCreateError && <span className={styles.inlineCreateError}>{inlineCreateError}</span>}
            </div>
        );
    };





    const openFileClick = (id: string, fType: "snippet" | "file") => {
        if (fType === "snippet") {
            const snipp = Snippets.find(s => s._id === id);
            if (!snipp) return;

            setOpenSnippets(prev =>
                prev.some(s => s._id === id) ? prev : [snipp, ...prev]
            );

            setOpenTabs(prev =>
                prev.some(tab => tab.resourceType === "snippet" && tab.data._id === id)
                    ? prev
                    : [{ resourceType: "snippet", data: snipp }, ...prev]
            );

            setActiveFileId({
                resourceType: "snippet",
                id: snipp._id,
            });

            setActiveSnippetId(snipp._id);
            setActiveLanguage(snipp.snippetLanguage || "");

            return;
        }

        const fil = Files.find(f => f._id === id);
        if (!fil) return;

        setOpenTabs(prev =>
            prev.some(tab => tab.resourceType === "file" && tab.data._id === id)
                ? prev
                : [{ resourceType: "file", data: fil }, ...prev]
        );

        setActiveFileId({
            resourceType: "file",
            id: fil._id,
        });
    };




    const folderClick = (id: string) => {
        setOpenMap(prev => ({ ...prev, [id]: !prev[id] }));

    };

    // read:
    const isOpen = (id: string) => !!openMap[id];


    const noFolder = (snip: Snippet) => snip.folderParent === null || snip.folderParent === undefined;
    const noFolderSnippets = Snippets.filter(noFolder);

    const nff = (fil: FileRef) => fil.folderParent === null || fil.folderParent === undefined;
    const noFolderFiles = Files.filter(nff);


    const fileById = useMemo(() => {
        const m = new Map<string, any>(); // replace any with FileRef type if you have it
        for (const f of Files) m.set(String(f._id), f);
        return m;
    }, [Files]);



    // function shortenFileName(name: string) {

    //     if (name.length > 20) {
    //         const ext = name.slice(name.indexOf('.'));
    //         let fName = name.slice(0, 16) + '---' + ext
    //         return fName
    //     } else {
    //         let fName = name;
    //         return fName
    //     }


    // }


    const renderFolder = (folder: any, depth = 0) => {
        const pad = depth * 12;

        return (
            <div
                key={folder._id}
                className={
                    `clickable ${styles.folderNode} ${isOpen(folder._id) ? styles.folderOpen : styles.folderClosed} ` +
                    `${(dragOverFolderId === folder._id || hoveredDropFolderId === folder._id) ? styles.folderDropTarget : ""}`
                }


            // style={{ paddingLeft: pad }}
            >
                <div className={`${styles.folderTop} ${activeFolderId === folder._id ? styles.folderActive : ""}`}
                    aria-expanded={isOpen(folder._id)}

                    data-folderid={String(folder._id)}

                    draggable
                    onDragStart={(e) => onFolderDragStart(e, folder)}
                    onDragEnd={onDragEnd}
                    onDrop={(e) => handleFolderDrop(e, folder._id)}

                    onContextMenu={(e) => openRightClickMenu(e, { name: folder.fname, id: folder._id, kind: "folder" })}


                >




                    <div onClick={(e) => folderSelectClick(e, folder._id)}
                        className={`${styles.resCheck} ${selectMode ? styles.resCheckVisible : ''} ${isChecked(folder._id) ? styles.resChecked : styles.rsUnchecked}`}
                    >

                        {isChecked(folder._id) ? (
                            <CheckIcon />
                        ) : (
                            <></>
                        )}

                    </div>

                    <div
                        className={styles.folderSelectZone}
                        onClick={(e) => { e.stopPropagation(); setActiveFolderId(folder._id); }}
                    >
                        <div style={{ width: pad }} />

                        <div
                            className={styles.folderOpenZone}
                            onClick={(e) => { e.stopPropagation(); setActiveFolderId(folder._id); folderClick(folder._id); }}
                        >
                            <img
                                draggable={false}
                                className="no-select"
                                src={`${process.env.PUBLIC_URL}/images/folder-${isOpen(folder._id) ? "open" : "closed"}-dark.png`}
                                width="15px"
                            />

                            {rename && nameChangeData?.id === folder._id ? (
                                <RenameTitle
                                    value={nameChangeData?.id === folder._id ? nameDraft : folder.fname}
                                    isEditing={rename && nameChangeData?.id === folder._id}
                                    onCancel={cancelRename}
                                    onCommit={(next) => commitRename(next)}
                                    onStartEdit={() => {
                                        setRename(true);
                                        setNameChangeData({ name: folder.fname, rType: "folder", id: folder._id });
                                        setNameDraft(folder.fname);
                                    }}
                                />
                            ) : (
                                <span className="p-none no-select">{folder.fname}</span>
                            )}

                            <div className={styles.folderExpandZone}>
                                <svg
                                    className={`${styles.folderShiv} no-select ${isOpen(folder._id) ? styles.openFShiv : styles.closedFShiv}`}
                                    data-name="Layer 1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 143.53 115.37"
                                >
                                    <path
                                        className="cls-1"
                                        d="M313.18,327.26h0a12.56,12.56,0,0,1-10.53-5.7l-7.46-11.43L254,247l-41.19,63.13-7.46,11.43a12.56,12.56,0,0,1-10.53,5.7h0a12.57,12.57,0,0,1-10.52-19.44l28.51-43.69L243,217.83a13.11,13.11,0,0,1,22,0l30.21,46.3,28.51,43.69A12.57,12.57,0,0,1,313.18,327.26Z"
                                        transform="translate(-182.23 -211.89)"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                {isOpen(folder._id) && (
                    <div className={styles.folderSnipsContainer}>
                        {renderInlineCreateRow(folder._id, depth + 1)}

                        {(folder.snippets ?? []).map((snipId: string) => {
                            const snip = snippetById.get(String(snipId));
                            if (!snip) return null;

                            return (
                                <div
                                    key={snip._id}
                                    className={`${styles.resSnipp} ${activeFileId?.id === snip._id ? styles.activeResSnipp : ""}`}
                                    draggable
                                    onDragStart={(e) => onSnippetDragStart(e, snip)}
                                    onDragEnd={onDragEnd}
                                    onClick={() => openFileClick(snip._id, 'snippet')}
                                    style={{ paddingLeft: 10 }}
                                    onContextMenu={(e) => openRightClickMenu(e, { name: snip.snippetName, id: snip._id, kind: "snippet" })}


                                >

                                    <div onClick={(e) => checkClick(e, snip._id, 'snippet')}
                                        className={`${styles.resCheck} ${selectMode ? styles.resCheckVisible : ''} ${isChecked(snip._id) ? styles.resChecked : styles.rsUnchecked}`}
                                    >

                                        {isChecked(snip._id) ? (
                                            <CheckIcon />
                                        ) : (
                                            <></>
                                        )}

                                    </div>



                                    <div className={styles.rsS1}>

                                        <img
                                            draggable={false}
                                            className="no-select"
                                            src={`${process.env.PUBLIC_URL}/images/${getSnippetIcon(snip.snippetLanguage || "txt")}.png`}
                                            height={14}
                                        />
                                        {

                                        }
                                        <span className={`p-none no-select ${styles.fileNameWrap}`}>

                                            {
                                                rename && nameChangeData?.id === snip._id ? (
                                                    <RenameTitle
                                                        value={nameChangeData?.id === snip._id ? nameDraft : snip.snippetName}
                                                        isEditing={rename && nameChangeData?.id === snip._id}
                                                        onCancel={cancelRename}
                                                        onCommit={(next) => commitRename(next)}
                                                        onStartEdit={() => {
                                                            setRename(true);
                                                            setNameChangeData({ name: snip.snippetName, rType: "snippet", id: snip._id });
                                                            setNameDraft(snip.snippetName);
                                                        }}
                                                    />
                                                ) : (
                                                    <span className={`p-none ${styles.fileName}`}>{snip.snippetName}.{getSnippetExtension(snip.snippetLanguage || 'txt')}</span>
                                                )
                                            }
                                        </span>


                                    </div>

                                    {openSnippets.some((s) => s._id === snip._id) ? <div className={styles.rsc} /> : null}


                                </div>
                            );
                        })}

                        {(folder.fileRefs ?? []).map((fileId: string) => {
                            const file = fileById.get(String(fileId));
                            if (!file) return null;


                            return (
                                <div
                                    className={`${styles.resSnipp} ${styles.rsFile} ${activeFileId?.id === file._id ? styles.activeResSnipp : ""}`}

                                    key={file._id}
                                    draggable
                                    onDragStart={(e) => onFileDragStart(e, file)}
                                    onDragEnd={onDragEnd}
                                    style={{ paddingLeft: 10 }}
                                    onClick={() => openFileClick(file._id, 'file')}
                                    onContextMenu={(e) => openRightClickMenu(e, { name: file.name, id: file._id, kind: "file" })}
                                >


                                    <div onClick={(e) => checkClick(e, file._id, 'file')}
                                        className={`${styles.resCheck} ${selectMode ? styles.resCheckVisible : ''} ${isChecked(file._id) ? styles.resChecked : styles.rsUnchecked}`}
                                    >

                                        {isChecked(file._id) ? (
                                            <CheckIcon />
                                        ) : (
                                            <></>
                                        )}

                                    </div>

                                    <div className={styles.rsS1}>
                                        <img draggable={false} src={`${process.env.PUBLIC_URL}/images/${getFileRefIcon(file.name)}`} />
                                        {/* <span>{shortenFileName(file.name)}</span> */}
                                        <span className={styles.fileName}>{file.name}</span>
                                    </div>


                                    {openTabs.some((f) => f.data._id === file._id) ? <div className={styles.rsc} /> : null}
                                </div>
                            );
                        })}

                        {(folder.folders ?? []).map((child: any) => renderFolder(child, depth + 1))}


                    </div>
                )}
            </div>
        );
    };
    //main return statement for component
    return (
        <div className='sb-tab-flx-cont'>




            <div id='mode-files'
                ref={filesTabRootRef}
            >

                {renderRightClickPopup()}
                <div id='resources' className='sb-mode-c'>
                    <div id='resources-top-bar'>
                        {/* <h3>Room Files</h3> */}

                        <h3><span>Flash</span> Editor</h3>

                        <div className={styles.sbQuickFlex}>

                            {selectMode ? (
                                <div className={`${styles.sbAddFileBtn} ${styles.sbCancelBtn}`}
                                    onClick={closeSelectModeClick}
                                    aria-label='Exit select mode'
                                    title="Cancel"
                                >
                                    <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                                        <g id="Layer_3" data-name="Layer 3">
                                            <path
                                                className="cls-1"
                                                d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                                transform="translate(-124.17 -181.62)"
                                            />
                                        </g>
                                    </svg>
                                    <span>Cancel</span>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.sbAddFileBtn}
                                        onClick={() => setSelectMode(true)}
                                        aria-label='Select Files'
                                        title="Select"
                                    >
                                        <SelectIcon />
                                    </div>

                                    {canAdd() && (
                                        <div className={styles.sbAddFileBtn}
                                            onClick={newFolderClick}
                                            aria-label='Add Folder'
                                            title="New Folder"
                                        >
                                            <NewFolderIcon />
                                        </div>
                                    )}

                                    {canAdd() && (
                                        <div className={styles.sbAddFileBtn}
                                            onClick={newSnipClick}
                                            aria-label='Add File'
                                            title="New File"
                                        >
                                            <AddFileIcon />
                                        </div>
                                    )}
                                </>
                            )}

                        </div>

                    </div>

                    <div className={`${styles.filesAndSnips} ${selectMode ? 'fas-select' : ''}`}
                        ref={filesAndSnipsRef}
                        onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest(`.${styles.resSnipp}, .${styles.folderTop}`)) return;
                            setActiveFolderId(null);
                        }}
                        onDragOver={(e) => {
                            if (!draggedItem) return;
                            e.preventDefault();
                            updateDragAutoScroll(e.clientY);

                            const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
                            const folderTop = el?.closest?.(`.${styles.folderTop}`) as HTMLElement | null;

                            if (!folderTop) {
                                setDragOverFolderId(null);
                                return;
                            }

                            const id = folderTop.dataset.folderid;
                            setDragOverFolderId(id && canDropIntoFolder(id, draggedItem) ? id : null);
                        }}


                        onDragLeave={() => {
                            setDragOverFolderId(null);
                            stopDragAutoScroll();
                        }}
                        onDrop={(e) => {
                            stopDragAutoScroll();
                            handleMainDrop(e);
                        }}


                    >

                        {renderInlineCreateRow(null)}

                        {
                            noFolderFiles.length ? (
                                <div>
                                    {

                                        noFolderFiles.map((file) => (
                                            <div
                                                className={`${styles.resSnipp} ${styles.rsFile} ${activeFileId?.id === file._id ? styles.activeResSnipp : ""}`}
                                                key={file._id}
                                                draggable
                                                onDragStart={(e) => onFileDragStart(e, file)}
                                                onDragEnd={onDragEnd}
                                                onClick={() => openFileClick(file._id, 'file')}
                                                onContextMenu={(e) => openRightClickMenu(e, { name: file.name, id: file._id, kind: "file" })}
                                            >

                                                <div onClick={(e) => checkClick(e, file._id, 'file')}
                                                    className={`${styles.resCheck} ${selectMode ? styles.resCheckVisible : ''} ${isChecked(file._id) ? styles.resChecked : styles.rsUnchecked}`}
                                                >

                                                    {isChecked(file._id) ? (
                                                        <CheckIcon />
                                                    ) : (
                                                        <></>
                                                    )}

                                                </div>


                                                <div className={styles.rsS1}>
                                                    <img draggable={false} src={`${process.env.PUBLIC_URL}/images/${getFileRefIcon(file.name)}`} />
                                                    {/* <span>{shortenFileName(file.name)}</span> */}
                                                    <span className={styles.fileName}>{file.name}</span>
                                                </div>


                                                {openTabs.some((f) => f.data._id === file._id) ? <div className={styles.rsc} /> : null}
                                            </div>
                                        ))

                                    }

                                </div>
                            ) : (
                                <></>
                            )
                        }

                        {noFolderSnippets.length ? (
                            <div>

                                {
                                    noFolderSnippets.map((snippet) => (
                                        <div
                                            draggable
                                            onDragStart={(e) => onSnippetDragStart(e, snippet)}
                                            onDragEnd={onDragEnd}

                                            onContextMenu={(e) => openRightClickMenu(e, { name: snippet.snippetName, id: snippet._id, kind: "snippet" })}

                                            key={snippet._id}
                                            className={`${styles.resSnipp} ${activeFileId?.id === snippet._id ? styles.activeResSnipp : ''}`}
                                            onClick={() => openFileClick(snippet._id, 'snippet')}

                                        >

                                            <div onClick={(e) => checkClick(e, snippet._id, 'snippet')}
                                                className={`${styles.resCheck} ${selectMode ? styles.resCheckVisible : ''} ${isChecked(snippet._id) ? styles.resChecked : styles.rsUnchecked}`}
                                            >

                                                {isChecked(snippet._id) ? (
                                                    <CheckIcon />
                                                ) : (
                                                    <></>
                                                )}

                                            </div>

                                            <div className={styles.rsS1}>
                                                <img draggable={false} src={`${process.env.PUBLIC_URL}/images/${getSnippetIcon(snippet.snippetLanguage || 'txt')}.png`} height={14} />
                                                {
                                                    rename && nameChangeData?.id === snippet._id ? (
                                                        <RenameTitle

                                                            value={nameChangeData?.id === snippet._id ? nameDraft : snippet.snippetName}
                                                            isEditing={rename && nameChangeData?.id === snippet._id}
                                                            onCancel={cancelRename}
                                                            onCommit={(next) => commitRename(next)}
                                                            onStartEdit={() => {
                                                                setRename(true);
                                                                setNameChangeData({ name: snippet.snippetName, rType: "snippet", id: snippet._id });
                                                                setNameDraft(snippet.snippetName);
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className={`p-none ${styles.fileName}`}>{snippet.snippetName}.{getSnippetExtension(snippet.snippetLanguage || 'txt')}</span>
                                                    )
                                                }

                                            </div>

                                            {openSnippets.some(s => s._id === snippet._id) ? (
                                                <div className={styles.rsc} />
                                            ) : (
                                                <></>
                                            )

                                            }
                                        </div>
                                    ))
                                }
                            </div>
                        ) : (
                            <> </>
                        )}

                        {Folders.map((folder) => renderFolder(folder, 0))}

                        {draggedItem &&
                            <div
                                onDrop={(e) => handleMainDrop(e)}
                                className={`out-of-folder-drag ${draggedItem ? styles.oofdA : styles.oofdU}`}
                            />
                        }

                    </div>



                    {selectMode && selectedIds.length > 0 &&

                        <div className='file-download-options fade-in'>
                            {confirmBulkDelete ? (
                                <div className='select-mode-btns'>
                                    <span className='selected-lbl'>Delete {deletableSelectedIds.length} item{deletableSelectedIds.length === 1 ? '' : 's'}?</span>

                                    <div className='quick-flex nbklsk'>
                                        <div onClick={() => setConfirmBulkDelete(false)} className='delete-pop-btn dp-c-s'>Cancel</div>
                                        <div onClick={() => void confirmBulkDeleteClick()} className='delete-pop-btn dp-y-s'>Yes, Delete</div>
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.selectModeToolbar}>

                                    <div className={styles.selectModeToolbarHeader}>
                                        <button onClick={selectAllClick} className={styles.selectAllTextBtn}>
                                            <SelectIcon />
                                            Select All
                                        </button>

                                        <button
                                            onClick={closeSelectModeClick}
                                            className={styles.selectModeToolbarCancel}
                                            aria-label='Exit select mode'
                                            title="Cancel"
                                        >
                                            <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                                                <g id="Layer_3" data-name="Layer 3">
                                                    <path
                                                        className="cls-1"
                                                        d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                                        transform="translate(-124.17 -181.62)"
                                                    />
                                                </g>
                                            </svg>
                                        </button>
                                    </div>

                                    <div className='quick-flex nbklsk'>

                                        <button
                                            className={`smb-download-selected ${selectedIds.length ? 'ok-to-download' : 'none-selected'}`}
                                            disabled={!selectedIds.length}
                                            onClick={downloadSelectedClick}
                                        >

                                            <Download />

                                            <span>
                                                Download
                                            </span>

                                        </button>

                                        {deletableSelectedIds.length > 0 && (
                                            <button
                                                className='smb-download-selected smb-delete-selected ok-to-download'
                                                onClick={bulkDeleteClick}
                                            >
                                                <TrashIcon />

                                                <span>
                                                    Delete
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    }
                </div>


                {/* 
                {canAdd() &&

                    <button className='upload-files-btn'

                        onClick={canAdd() ? newFileClick : undefined}
                    >
                        <img draggable={false} src={`${process.env.PUBLIC_URL}/images/add-folder-d.png`} width="20px" />
                        <span>Upload Files</span>
                    </button>

                } */}

            </div>

        </div>
    )
}

export default FileExplorerTab
